import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface TestInvoice {
  invoiceNumber: string;
  invoiceDate: Date;
  dueDate: Date;
  amount: number;
  distributor: string;
  description: string;
  status: 'new' | 'overdue' | 'autopay';
}

@Component({
  selector: 'app-test-add-invoices-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatTableModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>science</mat-icon>
      Test Add Invoices
    </h2>

    <mat-dialog-content>
      <div class="info-banner">
        <mat-icon>info</mat-icon>
        <p>
          This will generate and add 5 test invoices to customer <strong>{{ data.customerId }}</strong
          >. The invoices will have realistic dates and amounts with proper status calculation.
        </p>
      </div>

      <div class="table-container">
        <table mat-table [dataSource]="testInvoices()" class="test-invoices-table">
          <!-- Invoice Number Column -->
          <ng-container matColumnDef="invoiceNumber">
            <th mat-header-cell *matHeaderCellDef>Invoice #</th>
            <td mat-cell *matCellDef="let invoice">{{ invoice.invoiceNumber }}</td>
          </ng-container>

          <!-- Invoice Date Column -->
          <ng-container matColumnDef="invoiceDate">
            <th mat-header-cell *matHeaderCellDef>Invoice Date</th>
            <td mat-cell *matCellDef="let invoice">{{ invoice.invoiceDate | date : 'MMM dd, yyyy' }}</td>
          </ng-container>

          <!-- Due Date Column -->
          <ng-container matColumnDef="dueDate">
            <th mat-header-cell *matHeaderCellDef>Due Date</th>
            <td mat-cell *matCellDef="let invoice">
              <span [class.overdue]="invoice.status === 'overdue'">
                {{ invoice.dueDate | date : 'MMM dd, yyyy' }}
              </span>
            </td>
          </ng-container>

          <!-- Amount Column -->
          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef class="text-right">Amount</th>
            <td mat-cell *matCellDef="let invoice" class="text-right amount-column">
              {{ invoice.amount | currency }}
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let invoice">
              <span
                class="status-badge"
                [class.status-overdue]="invoice.status === 'overdue'"
                [class.status-new]="invoice.status === 'new'"
                [class.status-autopay]="invoice.status === 'autopay'"
              >
                {{ invoice.status | titlecase }}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>

      <div class="loading-overlay" *ngIf="isAdding()">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Adding test invoices...</p>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="isAdding()">Cancel</button>
      <button mat-stroked-button (click)="generateInvoices()" [disabled]="isAdding()">
        <mat-icon>refresh</mat-icon>
        Generate Again
      </button>
      <button mat-raised-button color="primary" (click)="onAdd()" [disabled]="isAdding()">
        <mat-icon>add</mat-icon>
        Add {{ testInvoices().length }} Invoices
      </button>
    </mat-dialog-actions>
  `,
})
export class TestAddInvoicesModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<TestAddInvoicesModalComponent>);
  public data: { customerId: string; enableAutopay?: boolean } = inject(MAT_DIALOG_DATA);

  testInvoices = signal<TestInvoice[]>([]);
  isAdding = signal<boolean>(false);

  displayedColumns: string[] = ['invoiceNumber', 'invoiceDate', 'dueDate', 'amount', 'status'];

  ngOnInit(): void {
    this.generateInvoices();
  }

  generateInvoices(): void {
    const invoices: TestInvoice[] = [];
    const today = new Date();

    for (let i = 0; i < 5; i++) {
      const invoiceNumber = this.generateInvoiceNumber();

      // Generate a mix of invoice ages for realistic overdue ratio (2-3 overdue, 2-3 new)
      let daysAgo: number;
      if (i < 2) {
        // First 2: older invoices (45-75 days ago)
        daysAgo = Math.floor(Math.random() * 30 + 45);
      } else if (i === 2) {
        // Middle invoice: random (could be either)
        daysAgo = Math.floor(Math.random() * 40 + 25);
      } else {
        // Last 2: recent invoices (5-25 days ago)
        daysAgo = Math.floor(Math.random() * 20 + 5);
      }

      const invoiceDate = new Date(today);
      invoiceDate.setDate(today.getDate() - daysAgo);

      // Due date is 30 days after invoice date
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(invoiceDate.getDate() + 30);

      // Determine status based on due date (compare dates only, not time)
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
      const isOverdue = dueDateOnly < todayDate;

      // If customer has autopay enabled and invoice is not overdue, status is 'autopay'
      // Otherwise, status is 'overdue' if past due date, or 'new' if not
      let status: 'new' | 'overdue' | 'autopay';
      if (isOverdue) {
        status = 'overdue';
      } else if (this.data.enableAutopay) {
        status = 'autopay';
      } else {
        status = 'new';
      }

      // Random amount between $100 and $10,000
      const amount = Math.floor(Math.random() * 9900 + 100);

      // Random distributor
      const distributors = ['VIT', 'CFS', 'ABC', 'XYZ', 'ACME'];
      const distributor = distributors[Math.floor(Math.random() * distributors.length)];

      invoices.push({
        invoiceNumber,
        invoiceDate,
        dueDate,
        amount,
        distributor,
        description: `Test invoice for account ${this.data.customerId}`,
        status,
      });
    }

    // Sort by invoice date (newest first)
    invoices.sort((a, b) => b.invoiceDate.getTime() - a.invoiceDate.getTime());

    this.testInvoices.set(invoices);
  }

  generateInvoiceNumber(): string {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0');
    return `INV-TEST${timestamp}${random}`;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    // Return the generated invoices to the parent component
    this.dialogRef.close(this.testInvoices());
  }
}
